<template src="./settings.pug" lang="pug"> </template>
<style src="./settings.scss" lang="scss" scoped> </style>

<script>
import { mapState, mapGetters } from 'vuex'
import { validationMixin } from 'vuelidate'
import { vLength } from '@/utils/helpers'
import { required, minLength } from 'vuelidate/lib/validators'
import validate from '@/mixins/validate'
import config from '../../../config.json'

export default {
    mixins: [validationMixin, validate],
    data: () => ({
        isShowGoogle: config.isShowGoogle,
        isShowFacebook: config.isShowFacebook,
        isFBDisabled: false,
        isGoogleDisabled: false,
        uploadsUrl: process.env.VUE_APP_BASE_API.replace(/v1\//, 'uploads'),
        canAgree: false,
        settings: {
            password: '',
            newPassword: '',
            confirmNewPassword: ''
        },
        userData: {
            phoneNumber: null,
            taxId: null,
            cardNumber: null,
            passportNumber: null,
            firstName: null,
            lastName: null,
            readTerms: false
        },
        updateDialog: false
    }),
    validations: {
        settings: {
            password: {
                required,
                minLength: minLength(6)
            },
            newPassword: {
                required,
                minLength: minLength(6)
            },
            confirmNewPassword: {
                required,
                equal: function() {
                    return this.settings.newPassword == this.settings.confirmNewPassword
                }
            }
        },
        details: {
            firstName: {
                required: function() {
                    return !!(this.userData.firstName && this.userData.firstName.trim())
                }
            },
            lastName: {
                required: function() {
                    return !!(this.userData.lastName && this.userData.lastName.trim())
                }
            },
            taxId: {
                required: function() {
                    return !!this.userData.taxId
                },
                length: function() {
                    return vLength(10)(this.userData.taxId)
                }
            },
            phoneNumber: {
                required: function() {
                    return !!this.phoneNumber
                },
                length: function() {
                    return vLength(9)(this.phoneNumber)
                }
            },
            cardNumber: {
                required: function() {
                    return !!this.userData.cardNumber
                },
                length: function() {
                    return vLength(16)(this.userData.cardNumber)
                }
            },
            passportNumber: {
                required: function() {
                    return !!this.userData.passportNumber
                }
            }
        }
    },
    computed: {
        ...mapGetters(['type']),
        ...mapState({
            fb: (state) => state.app.fb,
            google: (state) => state.app.google,
            user: (state) => state.app.user
        }),
        phoneNumber: {
            get() {
                return this.userData && this.userData.phoneNumber && this.userData.phoneNumber.replace(/^\+380/, '')
            },
            set(val) {
                this.$set(this.userData, 'phoneNumber', `+380${val}`)
            }
        },
        disableMainData() {
            return this.user && this.user.status && ['check'].includes(this.user.status)
        },
        termsLabel() {
            const path = '$vuetify.pages.Settings'
            const agreedWith = this.$vuetify.t(`${path}.agreedWith`)
            const termsText = this.$vuetify.t(`${path}.termsOfUsage`)

            return `<span>${agreedWith} <a href="${this.uploadsUrl}/terms.pdf" target="_blank" download>${termsText}</a></span>`
        }
    },
    created() {
        this.userData = JSON.parse(JSON.stringify(this.user))
    },
    methods: {
        async linkFBAccount() {
            this.isFBDisabled = true
            await FB.getLoginStatus(async(response) => {
                if (response.status === 'connected') {
                    await this.$store.dispatch('socials/linkFBAccount', response.authResponse.accessToken)
                } else {
                    await FB.login(async(response) => {
                        if (response.authResponse && response.authResponse.accessToken) {
                            await this.$store.dispatch('socials/linkFBAccount', response.authResponse.accessToken)
                        }
                    })
                }
                this.isFBDisabled = false
            })
        },
        async unlinkFBAccount() {
            this.isFBDisabled = true
            await this.$store.dispatch('socials/unlinkFBAccount')
            this.isFBDisabled = false
        },
        async onFBSwitchChange(event) {
            if (event.target.checked) {
                await this.linkFBAccount()
            } else {
                await this.unlinkFBAccount()
            }
            event.target.checked = this.fb
        },
        async linkGoogle() {
            this.isGoogleDisabled = true

            this.$gapi._libraryLoad('auth2')
                .then(auth2 => {
                    auth2.init(this.$gapi.config)
                        .then(async auth2 => {
                            const auth = await auth2.isSignedIn.get()
                            if (!auth) {
                                await auth2.signIn().catch(_ => {})
                            }
                            const accessToken = auth2.currentUser.get().getAuthResponse().id_token
                            if (accessToken) {
                                await this.$store.dispatch('socials/linkGoogleAccount', accessToken)
                            }
                            this.isGoogleDisabled = false
                        })
                        .catch(_ => {})
                })
        },
        async unlinkGoogle() {
            this.isGoogleDisabled = true
            this.$gapi._libraryLoad('auth2')
                .then(auth2 => {
                    auth2.init(this.$gapi.config)
                        .then(async auth2 => {
                            const auth = await auth2.isSignedIn.get()
                            if (auth) {
                                await auth2.signOut().catch(_ => {})
                            }
                            await this.$store.dispatch('socials/unlinkGoogleAccount')
                            this.isGoogleDisabled = false
                        })
                        .catch(_ => {})
                })
        },
        async onGoogleSwitchChange(event) {
            if (event.target.checked) {
                await this.linkGoogle()
            } else {
                await this.unlinkGoogle()
            }
            event.target.checked = this.google
        },
        changePassword() {
            if (this.$v.settings.$invalid) {
                this.$v.settings.$touch()
            } else {
                this.$store.dispatch('socials/changePassword', {
                    password: this.settings.password,
                    newPassword: this.settings.newPassword,
                    confirmNewPassword: this.settings.confirmNewPassword
                })
                this.resetPasswords()
            }
        },
        resetPasswords() {
            this.settings = {
                password: '',
                newPassword: '',
                confirmNewPassword: ''
            }
            this.$nextTick(_ => {
                this.$v.settings.$reset()
            })
        },
        updateAccount() {
            const props = ['firstName', 'lastName', 'taxId', 'cardNumber', 'passportNumber', 'readTerms']

            this.$store.dispatch('socials/updateAccount', Object.assign({}, ...props.map(it => {
                return { [it]: this.userData[it] || null }
            }), {
                phoneNumber: this.userData.phoneNumber || null
            }))

            this.updateDialog = false
        },

        confirmUpdate() {
            if (this.$v.details.$invalid) {
                this.$v.details.$touch()
            } else {
                this.updateDialog = true
            }
        }
    }
}
</script>
